/**
 * Error Classification Utility
 * Categorizes different error types and provides recovery strategies
 *
 * Features:
 * - Comprehensive error type classification
 * - Actionable recovery recommendations
 * - Retryability determination
 * - User-friendly error messages
 */

/**
 * Classified error types
 */
export enum ErrorType {
  // Network and connectivity errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  DNS_RESOLUTION_ERROR = 'DNS_RESOLUTION_ERROR',
  CONNECTION_REFUSED = 'CONNECTION_REFUSED',

  // HTTP client errors (4xx)
  BAD_REQUEST = 'BAD_REQUEST',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  CONFLICT = 'CONFLICT',
  GONE = 'GONE',
  TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',

  // HTTP server errors (5xx)
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  BAD_GATEWAY = 'BAD_GATEWAY',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  GATEWAY_TIMEOUT = 'GATEWAY_TIMEOUT',

  // Parsing and validation errors
  PARSE_ERROR = 'PARSE_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SERIALIZATION_ERROR = 'SERIALIZATION_ERROR',

  // Request-specific errors
  REQUEST_CANCELLED = 'REQUEST_CANCELLED',
  REQUEST_INVALID = 'REQUEST_INVALID',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',

  // Unknown or unclassified
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Recovery action types
 */
export enum RecoveryAction {
  // Automatic actions
  RETRY = 'RETRY',
  RETRY_WITH_BACKOFF = 'RETRY_WITH_BACKOFF',
  CIRCUIT_BREAK = 'CIRCUIT_BREAK',

  // Manual/user actions
  CHECK_NETWORK = 'CHECK_NETWORK',
  REFRESH_PAGE = 'REFRESH_PAGE',
  CLEAR_CACHE = 'CLEAR_CACHE',
  LOGOUT_AND_LOGIN = 'LOGOUT_AND_LOGIN',
  CONTACT_SUPPORT = 'CONTACT_SUPPORT',
  CHECK_PERMISSIONS = 'CHECK_PERMISSIONS',
  UPDATE_PAYLOAD = 'UPDATE_PAYLOAD',

  // No action needed
  NO_ACTION = 'NO_ACTION',
}

/**
 * Classified error information
 */
export interface ClassifiedError {
  /** Error type classification */
  type: ErrorType;
  /** HTTP status code (if applicable) */
  statusCode?: number;
  /** Error message for users */
  userMessage: string;
  /** Technical error message */
  technicalMessage: string;
  /** Whether the error is retryable */
  isRetryable: boolean;
  /** Maximum retry attempts (0 = don't retry) */
  maxRetries: number;
  /** Recommended recovery action */
  primaryAction: RecoveryAction;
  /** Additional recovery actions */
  additionalActions: RecoveryAction[];
  /** Error timestamp */
  timestamp: Date;
  /** Unique error ID for tracking */
  errorId: string;
}

/**
 * Error Classifier
 * Analyzes errors and provides structured classification
 */
export class ErrorClassifier {
  /**
   * Generate unique error ID for tracking
   */
  private static generateErrorId(): string {
    return `err-${Date.now()}-${Math.random().toString(36).substring(7)}`;
  }

  /**
   * Classify an unknown error
   */
  static classify(error: unknown, statusCode?: number): ClassifiedError {
    // Check if already classified
    if (this.isClassified(error)) {
      return error;
    }

    // Handle HTTP status codes
    if (statusCode) {
      return this.classifyByStatusCode(statusCode);
    }

    // Handle Error objects
    if (error instanceof Error) {
      return this.classifyError(error);
    }

    // Handle strings
    if (typeof error === 'string') {
      return this.classifyString(error);
    }

    // Unknown
    return this.createClassification(
      ErrorType.UNKNOWN_ERROR,
      'An unexpected error occurred',
      'Unknown error type: ' + typeof error,
      false,
      0,
      RecoveryAction.CONTACT_SUPPORT
    );
  }

  /**
   * Classify by HTTP status code
   */
  private static classifyByStatusCode(statusCode: number): ClassifiedError {
    switch (statusCode) {
      // 4xx Client Errors
      case 400:
        return this.createClassification(
          ErrorType.BAD_REQUEST,
          'Invalid request format',
          'Server rejected the request due to bad syntax',
          false,
          0,
          RecoveryAction.UPDATE_PAYLOAD
        );

      case 401:
        return this.createClassification(
          ErrorType.UNAUTHORIZED,
          'Authentication required',
          'Your session has expired. Please log in again.',
          true,
          1,
          RecoveryAction.LOGOUT_AND_LOGIN,
          [RecoveryAction.REFRESH_PAGE]
        );

      case 403:
        return this.createClassification(
          ErrorType.FORBIDDEN,
          'Access denied',
          'You do not have permission to access this resource',
          false,
          0,
          RecoveryAction.CHECK_PERMISSIONS,
          [RecoveryAction.CONTACT_SUPPORT]
        );

      case 404:
        return this.createClassification(
          ErrorType.NOT_FOUND,
          'Resource not found',
          'The requested resource does not exist',
          false,
          0,
          RecoveryAction.CHECK_PERMISSIONS,
          [RecoveryAction.REFRESH_PAGE]
        );

      case 409:
        return this.createClassification(
          ErrorType.CONFLICT,
          'Conflict with current state',
          'The request conflicts with the current state (e.g., duplicate resource)',
          false,
          0,
          RecoveryAction.REFRESH_PAGE,
          [RecoveryAction.RETRY_WITH_BACKOFF]
        );

      case 410:
        return this.createClassification(
          ErrorType.GONE,
          'Resource no longer available',
          'The requested resource is permanently gone',
          false,
          0,
          RecoveryAction.NO_ACTION
        );

      case 413:
        return this.createClassification(
          ErrorType.PAYLOAD_TOO_LARGE,
          'Request too large',
          'The request payload exceeds the size limit',
          false,
          0,
          RecoveryAction.UPDATE_PAYLOAD
        );

      case 429:
        return this.createClassification(
          ErrorType.TOO_MANY_REQUESTS,
          'Rate limit exceeded',
          'You have sent too many requests. Please wait before retrying.',
          true,
          3,
          RecoveryAction.RETRY_WITH_BACKOFF
        );

      // 5xx Server Errors
      case 500:
        return this.createClassification(
          ErrorType.INTERNAL_SERVER_ERROR,
          'Server error',
          'The server encountered an unexpected error',
          true,
          3,
          RecoveryAction.RETRY_WITH_BACKOFF,
          [RecoveryAction.CONTACT_SUPPORT]
        );

      case 502:
        return this.createClassification(
          ErrorType.BAD_GATEWAY,
          'Service temporarily unavailable',
          'Bad gateway - the server is temporarily unavailable',
          true,
          3,
          RecoveryAction.RETRY_WITH_BACKOFF
        );

      case 503:
        return this.createClassification(
          ErrorType.SERVICE_UNAVAILABLE,
          'Service unavailable',
          'The server is temporarily unavailable for maintenance',
          true,
          3,
          RecoveryAction.RETRY_WITH_BACKOFF
        );

      case 504:
        return this.createClassification(
          ErrorType.GATEWAY_TIMEOUT,
          'Gateway timeout',
          'The server took too long to respond',
          true,
          2,
          RecoveryAction.RETRY_WITH_BACKOFF
        );

      // Default for other status codes
      default:
        if (statusCode >= 500) {
          return this.createClassification(
            ErrorType.INTERNAL_SERVER_ERROR,
            'Server error',
            `Server error (HTTP ${statusCode})`,
            true,
            2,
            RecoveryAction.RETRY_WITH_BACKOFF
          );
        } else if (statusCode >= 400) {
          return this.createClassification(
            ErrorType.BAD_REQUEST,
            'Invalid request',
            `Client error (HTTP ${statusCode})`,
            false,
            0,
            RecoveryAction.UPDATE_PAYLOAD
          );
        }
    }

    return this.createClassification(
      ErrorType.UNKNOWN_ERROR,
      'Unexpected error',
      `HTTP error ${statusCode}`,
      false,
      0,
      RecoveryAction.CONTACT_SUPPORT
    );
  }

  /**
   * Classify Error object
   */
  private static classifyError(error: Error): ClassifiedError {
    const message = error.message.toLowerCase();
    const name = error.name.toLowerCase();

    // Timeout errors
    if (
      name.includes('timeout') ||
      message.includes('timeout') ||
      message.includes('timed out')
    ) {
      return this.createClassification(
        ErrorType.TIMEOUT_ERROR,
        'Request timeout',
        'The server took too long to respond',
        true,
        2,
        RecoveryAction.RETRY_WITH_BACKOFF,
        [RecoveryAction.CHECK_NETWORK]
      );
    }

    // Network errors
    if (
      name === 'typeerror' &&
      message.includes('fetch')
    ) {
      return this.createClassification(
        ErrorType.NETWORK_ERROR,
        'Network connection failed',
        'Unable to reach the server. Check your internet connection.',
        true,
        3,
        RecoveryAction.CHECK_NETWORK,
        [RecoveryAction.REFRESH_PAGE]
      );
    }

    // DNS resolution errors
    if (message.includes('enotfound') || message.includes('getaddrinfo')) {
      return this.createClassification(
        ErrorType.DNS_RESOLUTION_ERROR,
        'Failed to resolve server',
        'The server address could not be resolved',
        true,
        2,
        RecoveryAction.CHECK_NETWORK
      );
    }

    // Connection refused
    if (message.includes('econnrefused') || message.includes('connection refused')) {
      return this.createClassification(
        ErrorType.CONNECTION_REFUSED,
        'Connection refused',
        'The server refused the connection',
        true,
        2,
        RecoveryAction.CHECK_NETWORK,
        [RecoveryAction.RETRY_WITH_BACKOFF]
      );
    }

    // Abort/cancellation errors
    if (name === 'aborterror' || message.includes('cancelled')) {
      return this.createClassification(
        ErrorType.REQUEST_CANCELLED,
        'Request cancelled',
        'The request was cancelled',
        false,
        0,
        RecoveryAction.RETRY
      );
    }

    // JSON parsing errors
    if (name === 'syntaxerror' || message.includes('json')) {
      return this.createClassification(
        ErrorType.PARSE_ERROR,
        'Failed to parse response',
        'The server response was not valid JSON',
        false,
        0,
        RecoveryAction.CONTACT_SUPPORT
      );
    }

    // Generic TypeError
    if (name === 'typeerror') {
      return this.createClassification(
        ErrorType.VALIDATION_ERROR,
        'Invalid data format',
        error.message,
        false,
        0,
        RecoveryAction.UPDATE_PAYLOAD
      );
    }

    // Default
    return this.createClassification(
      ErrorType.UNKNOWN_ERROR,
      'An error occurred',
      error.message,
      false,
      0,
      RecoveryAction.CONTACT_SUPPORT
    );
  }

  /**
   * Classify string error
   */
  private static classifyString(error: string): ClassifiedError {
    const lower = error.toLowerCase();

    if (lower.includes('network') || lower.includes('offline')) {
      return this.createClassification(
        ErrorType.NETWORK_ERROR,
        'Network connection failed',
        error,
        true,
        2,
        RecoveryAction.CHECK_NETWORK
      );
    }

    return this.createClassification(
      ErrorType.UNKNOWN_ERROR,
      'An error occurred',
      error,
      false,
      0,
      RecoveryAction.CONTACT_SUPPORT
    );
  }

  /**
   * Check if error is already classified
   */
  private static isClassified(error: unknown): error is ClassifiedError {
    if (typeof error !== 'object' || error === null) {
      return false;
    }

    const obj = error as any;
    return (
      'type' in obj &&
      'userMessage' in obj &&
      'technicalMessage' in obj &&
      'isRetryable' in obj
    );
  }

  /**
   * Create classification object
   */
  private static createClassification(
    type: ErrorType,
    userMessage: string,
    technicalMessage: string,
    isRetryable: boolean,
    maxRetries: number,
    primaryAction: RecoveryAction,
    additionalActions: RecoveryAction[] = []
  ): ClassifiedError {
    return {
      type,
      userMessage,
      technicalMessage,
      isRetryable,
      maxRetries,
      primaryAction,
      additionalActions,
      timestamp: new Date(),
      errorId: this.generateErrorId(),
    };
  }

  /**
   * Get recovery actions for a classified error
   */
  static getRecoveryActions(classified: ClassifiedError): RecoveryAction[] {
    return [classified.primaryAction, ...classified.additionalActions];
  }

  /**
   * Format error for user display
   */
  static formatForUser(classified: ClassifiedError): string {
    const parts = [classified.userMessage];

    if (classified.additionalActions.length > 0) {
      const actions = classified.additionalActions
        .map((action) => this.actionToString(action))
        .join(' or ');
      parts.push(`Try ${actions}.`);
    }

    return parts.join(' ');
  }

  /**
   * Convert action to user-friendly string
   */
  private static actionToString(action: RecoveryAction): string {
    const actionMap: Record<RecoveryAction, string> = {
      [RecoveryAction.RETRY]: 'again',
      [RecoveryAction.RETRY_WITH_BACKOFF]: 'again in a moment',
      [RecoveryAction.CIRCUIT_BREAK]: 'contacting support',
      [RecoveryAction.CHECK_NETWORK]: 'checking your network connection',
      [RecoveryAction.REFRESH_PAGE]: 'refreshing the page',
      [RecoveryAction.CLEAR_CACHE]: 'clearing your cache',
      [RecoveryAction.LOGOUT_AND_LOGIN]: 'logging out and back in',
      [RecoveryAction.CONTACT_SUPPORT]: 'contacting support',
      [RecoveryAction.CHECK_PERMISSIONS]: 'checking your permissions',
      [RecoveryAction.UPDATE_PAYLOAD]: 'updating your request',
      [RecoveryAction.NO_ACTION]: 'waiting',
    };

    return actionMap[action] || 'trying again';
  }
}
